const OBJECT_ID_REGEX = /^[0-9a-fA-F]{24}$/;

export function isPollMessage(message = {}) {
	return message?.type === "poll" || Boolean(message?.pollId || message?.poll?._id);
}

export function getPollId(message = {}) {
	return message?.poll?._id || message?.pollId || null;
}

export function getMessageTimestamp(message = {}) {
	const directTimestamp = message?.created || message?.createdAt || message?.updatedAt;
	if (directTimestamp)
	{
		return directTimestamp;
	}

	const objectIdValue = String(message?._id || "");
	if (OBJECT_ID_REGEX.test(objectIdValue))
	{
		return new Date(parseInt(objectIdValue.slice(0, 8), 16) * 1000).toISOString();
	}

	return new Date().toISOString();
}

export function stripHtml(value = "") {
	return String(value || "")
		.replace(/<[^>]*>/g, " ")
		.replace(/\s+/g, " ")
		.trim();
}

export function buildPollPreviewLabel(message = {}) {
	return (
		message?.previewText ||
		message?.poll?.question ||
		stripHtml(message?.body) ||
		"Poll"
	);
}

export function buildForwardPollSnapshot(message = {}) {
	const poll = message?.poll;
	if (!poll) {
		return (
			message?.forward?.pollSnapshot || {
				question: buildPollPreviewLabel(message),
				description: "",
				mode: "regular",
				totalVoterCount: 0,
				isClosed: false,
				isAnonymous: true,
				allowsMultipleAnswers: false,
				options: []
			}
		);
	}

	return {
		question: poll.question,
		description: poll.description || "",
		mode: poll.mode || "regular",
		totalVoterCount: poll.totalVoterCount ?? 0,
		isClosed: poll.isClosed === true,
		isAnonymous: poll.isAnonymous === true,
		allowsMultipleAnswers: poll.allowsMultipleAnswers === true,
		options: (poll.options || []).map((option) => ({
			text: option.text,
			voteCount: option.voteCount ?? 0,
			isCorrect: option.isCorrect === true
		}))
	};
}

export async function hydratePollMessages(messages = [], requestOptions = {}) {
	if (!Array.isArray(messages) || messages.length === 0) {
		return messages;
	}

	const pollIds = [...new Set(messages.map((message) => getPollId(message)).filter(Boolean))];
	if (!pollIds.length) {
		return messages;
	}

	try {
		const [{ post }, { default: config }] = await Promise.all([
			import("../../../helpers/api"),
			import("../../../config")
		]);
		const batch = await post(config.api.chat.pollsBatch, { pollIds }, requestOptions);
		const pollByMessageId = new Map(
			(batch || []).map((item) => [String(item.messageId), item.poll])
		);
		const pollByPollId = new Map(
			(batch || []).map((item) => [String(item.pollId), item.poll])
		);

		return messages.map((message) => {
			if (!isPollMessage(message)) return message;
			const pollId = String(getPollId(message) || "");
			return {
				...message,
				type: "poll",
				pollId: pollId || message?.pollId || null,
				poll:
					pollByMessageId.get(String(message?._id || "")) ||
					pollByPollId.get(pollId) ||
					message?.poll ||
					null
			};
		});
	} catch (error) {
		console.error("Failed to hydrate poll messages:", error);
		return messages;
	}
}

export function emitSocketAck(socket, eventName, payload = {}) {
	return new Promise((resolve, reject) => {
		if (!socket) {
			reject(new Error("Socket is not available."));
			return;
		}

		socket.emit(eventName, payload, (response = {}) => {
			if (response?.ok) {
				resolve(response);
				return;
			}

			reject(new Error(response?.error?.message || "Poll request failed."));
		});
	});
}
